import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph } from '@digdir/designsystemet-react';
import type { WorkEnvironmentDocumentation } from '../../../api/workEnvironmentTypes';
import { fullDate } from '../../HRDashboard/hmsFormat';
import {
  generatedText,
  headerFacts,
  lockedText,
  remaining,
  STATUS_COLORS,
  summaryText,
  tally,
} from '../inspectionReportView';
import { ReportSection, StatusTag } from './ReportSection';
import classes from '../InspectionReport.module.css';

type Props = { report: WorkEnvironmentDocumentation };

type HeaderProps = Props & {
  /** Inside the evaluation the page already has a main heading */
  titleLevel: 1 | 2;
};

const K = 'workEnvironmentInspection';
const HOW = ['requirements', 'steps', 'anonymous'] as const;

/** The cover with the facts about the business and the team, and how to read the report */
export const ReportHeader = ({ report, titleLevel }: HeaderProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const locked = lockedText(report, t, i18n.language);
  return (
    <>
      <header className={classes.header}>
        <div className={classes.eyebrowRow}>
          <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
            {t(`${K}.header.eyebrow`)}
          </span>
        </div>
        <div className={classes.titleBlock}>
          <Heading level={titleLevel} data-size="xl" id="report-title">
            {t(`${K}.header.title`, { team: report.team.name })}
          </Heading>
          <Paragraph data-size="lg">
            {t(`${K}.header.subtitle`, {
              date: fullDate(report.survey.createdAt, i18n.language),
              business: report.business.name,
            })}
          </Paragraph>
        </div>
        <dl className={classes.facts}>
          {headerFacts(report, t, i18n.language).map((fact) => (
            <div key={fact.key} className={classes.fact}>
              <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                {fact.label}
              </dt>
              <dd className="ds-paragraph" data-size="sm">
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
        <Paragraph data-size="sm" className={classes.subtle}>
          {generatedText(report, t, i18n.language)} {t(`${K}.header.note`)}
        </Paragraph>
        {locked && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">{locked}</Paragraph>
          </Alert>
        )}
      </header>
      <ReportSection id="report-how" part={null} title={t(`${K}.parts.how`)}>
        <ol className={classes.howList}>
          {HOW.map((key, index) => (
            <li key={key}>
              <span aria-hidden="true" className={classes.number}>
                {index + 1}
              </span>
              <span className="ds-label" data-size="sm">
                {t(`${K}.how.items.${key}.title`)}
              </span>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="sm">
                {t(`${K}.how.items.${key}.text`)}
              </span>
            </li>
          ))}
        </ol>
      </ReportSection>
    </>
  );
};

/** Part 1: how many requirements are met, and what remains with owner and deadline */
export const ReportSummary = ({ report }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const items = remaining(report, t, i18n.language);
  return (
    <ReportSection id="report-summary" part={1} title={t(`${K}.parts.summary`)}>
      <Paragraph className={classes.lead}>{summaryText(report, t)}</Paragraph>
      <ul aria-label={t(`${K}.parts.summary`)} className={classes.tally}>
        {tally(report).map((item) => (
          <li key={item.status}>
            <span className="ds-heading" data-size="lg">
              {t(`${K}.tally`, { count: item.count })}
            </span>
            <StatusTag label={t(`${K}.status.${item.status}`)} color={STATUS_COLORS[item.status]} />
          </li>
        ))}
      </ul>
      <div className={classes.group}>
        <Heading level={3} data-size="xs">
          {t(`${K}.parts.remaining`)}
        </Heading>
        {items.length === 0 ? (
          <Paragraph data-size="sm">{t(`${K}.noneRemaining`)}</Paragraph>
        ) : (
          <ul className={classes.plainList}>
            {items.map((item) => (
              <li key={item.key} className={classes.row}>
                <span className={classes.rowText}>
                  <span className="ds-label" data-size="sm">
                    {item.title}
                  </span>
                  <span className="ds-paragraph" data-size="sm">
                    {item.fix}
                  </span>
                  <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                    {item.meta}
                  </span>
                </span>
                <StatusTag label={item.status} color={item.color} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </ReportSection>
  );
};
